// The floating-bar waterfall: the current rate, one bar per block with its +points,
// the dashed 98% line, an "If all closed" bar, and then the gap to 100% decomposed
// into named categories (contractual settled below expected, write-offs, timely
// filing, final underpayments below the appeal threshold, the patient's share open,
// no reason code from the feed, codes no block maps) as muted bars with their
// dollars and claim counts, so each can be worked (David, 2026-10-16: a computed
// breakdown, not a prose excuse), and a last "Other" bar immediately left of 100%
// that absorbs whatever the named bars do not (the breakdown's own "other" category,
// rounding), so the walk always ends at exactly 100% (David, 2026-10-19; the page's
// `walk`). Inline SVG, no chart library (the design preview's chart, drawn from the
// live payload). Every block bar is a button: clicking it opens the same modal as the
// block's table row; the Other bar opens its components.

import React from "react";

const W = 760;
const H = 260;
const PX = 44;
const PT = 18;
const PB = 46;

export const FILL = {
  bad: "#b3261e",
  warn: "#b26a00",
  info: "#2d4f8a",
  muted: "#5f6b69",
  accent: "#176b5a",
  ok: "#1f7a4d",
};

// The short label under a bar, broken onto two lines at the middle word so a block
// name never runs into its neighbour.
export function splitLabel(label) {
  const words = String(label || "")
    .split(/\s+/)
    .filter(Boolean);
  if (words.length < 2) return [words[0] || "", ""];
  const cut = Math.ceil(words.length / 2);
  return [words.slice(0, cut).join(" "), words.slice(cut).join(" ")];
}

// The chart's geometry, exported so the test can check a bar per block.
export function chartSteps(start, blocks) {
  let cur = Number(start) || 0;
  return (blocks || []).map((b) => {
    const pts = Number(b.to_98) || 0;
    const from = cur;
    const to = Math.min(cur + pts, 100);
    cur = to;
    return {
      key: b.key,
      label: b.label,
      fill: b.fill,
      pts,
      from,
      to,
      resolved: !!b.resolved,
    };
  });
}

// The breakdown's bars, stacked on from "If all closed" and clamped at 100.
export function breakdownSteps(end, categories) {
  let cur = Number(end) || 0;
  return (categories || [])
    .filter((c) => Number(c.dollars) > 0.005)
    .map((c) => {
      const pts = Number(c.points) || 0;
      const from = cur;
      const to = Math.min(cur + pts, 100);
      cur = to;
      return {
        key: c.key,
        label: c.label,
        pts,
        from,
        to,
        dollars: Number(c.dollars) || 0,
        claims: Number(c.claims) || 0,
      };
    });
}

// The walk's gap bars (the page's `walk.bars` of kind "gap": the named categories,
// never "other"), stacked on from "If all closed" and clamped at 100.
export function walkGapSteps(end, walk) {
  let cur = Number(end) || 0;
  return ((walk && walk.bars) || [])
    .filter((b) => b.kind === "gap" && Number(b.dollars) > 0.005)
    .map((b) => {
      const pts = Number(b.points) || 0;
      const from = cur;
      const to = Math.min(cur + pts, 100);
      cur = to;
      return {
        key: b.key,
        label: b.label,
        pts,
        from,
        to,
        dollars: Number(b.dollars) || 0,
        claims: Number(b.claims) || 0,
      };
    });
}

// The last bar: from where the named bars end to 100, whatever that takes.
export function otherStep(end, walk) {
  if (!walk || !walk.other) return null;
  const from = Math.min(Number(end) || 0, 100);
  return {
    key: "other",
    pts: Number(walk.other.points) || 0,
    from,
    to: 100,
    dollars: Number(walk.other.dollars) || 0,
    components: walk.other.components || [],
  };
}

const shortLabel = (key) =>
  ({
    contractual_below_expected: "Settled below expected",
    write_offs: "Write-offs",
    timely_filing: "Timely filing",
    below_appeal_threshold: "Below appeal threshold",
    patient_share_open: "Patient share open",
    no_reason_code: "No code from feed",
    unmapped_reason_code: "Codes no block maps",
    other: "Other, after every block",
  })[key] || key;

export default function WaterfallChart({
  start,
  blocks,
  target = 98,
  onSelect,
  breakdown = null,
  walk = null,
}) {
  const rate = start === null || start === undefined ? null : Number(start);
  const steps = chartSteps(rate || 0, blocks);
  const closed = steps.length ? steps[steps.length - 1].to : rate || 0;
  const gaps = walk
    ? walkGapSteps(closed, walk)
    : breakdownSteps(closed, breakdown && breakdown.categories);
  const other = otherStep(gaps.length ? gaps[gaps.length - 1].to : closed, walk);
  const n = steps.length + 2 + gaps.length + (other ? 1 : 0);
  const bw = (W - PX - 16) / n;
  const lo = Math.max(0, Math.floor(Math.min(rate || 0, 50) / 10) * 10 - 10);
  const hi = 104;
  const y = (v) =>
    PT + (H - PT - PB) * (1 - (Math.min(Math.max(v, lo), hi) - lo) / (hi - lo));
  const end = steps.length ? steps[steps.length - 1].to : rate || 0;
  // With the walk, the Other bar takes the walk to 100 and nothing is unexplained.
  const unexplained = walk ? 0 : Math.max(target - end, 0);

  const grid = [lo, lo + 10, lo + 20, lo + 30, lo + 40, lo + 50, 100]
    .filter((v) => v <= 100)
    .map((v) => (
      <g key={`g${v}`}>
        <line x1={PX} x2={W - 8} y1={y(v)} y2={y(v)} className="wf-grid" />
        <text
          x={PX - 6}
          y={y(v) + 4}
          fontSize="10"
          textAnchor="end"
          className="wf-muted"
        >
          {v}%
        </text>
      </g>
    ));

  const bar = (i, v0, v1, fill, label, sub, key, extra) => {
    const x = PX + i * bw + bw * 0.12;
    const w = bw * 0.76;
    const top = Math.min(y(v0), y(v1));
    const h = Math.max(Math.abs(y(v0) - y(v1)), 2);
    const clickable = Boolean(key && onSelect);
    return (
      <g
        key={key || `bar${i}`}
        className={clickable ? "wf-bar-group" : undefined}
      >
        <rect
          className={clickable ? "wf-bar wf-bar-clickable" : "wf-bar"}
          data-block={key || undefined}
          x={x}
          y={top}
          width={w}
          height={h}
          rx="2"
          fill={fill}
          role={clickable ? "button" : undefined}
          tabIndex={clickable ? 0 : undefined}
          aria-label={
            clickable ? `Open the claims in ${sub.join(" ")}` : undefined
          }
          onClick={clickable ? () => onSelect(key) : undefined}
          onKeyDown={
            clickable
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(key);
                  }
                }
              : undefined
          }
        >
          {clickable && <title>Open the claims in this block</title>}
        </rect>
        <text
          x={x + w / 2}
          y={top - 5}
          fontSize="11"
          fontWeight="600"
          textAnchor="middle"
          className="wf-fg"
        >
          {label}
        </text>
        <text
          x={x + w / 2}
          y={H - PB + 14}
          fontSize="10"
          textAnchor="middle"
          className="wf-muted"
        >
          {sub[0]}
        </text>
        {sub[1] ? (
          <text
            x={x + w / 2}
            y={H - PB + 26}
            fontSize="10"
            textAnchor="middle"
            className="wf-muted"
          >
            {sub[1]}
          </text>
        ) : null}
        {extra}
      </g>
    );
  };

  const parts = [];
  parts.push(
    bar(
      0,
      lo,
      rate || 0,
      FILL.accent,
      rate === null ? "—" : `${Math.round(rate)}%`,
      ["Current", "collections"],
      "today",
    ),
  );
  steps.forEach((st, i) => {
    const xa = PX + (i + 1) * bw + bw * 0.12;
    const [l1, l2] = splitLabel(st.label);
    parts.push(
      bar(
        i + 1,
        st.from,
        st.to,
        st.resolved ? FILL.muted : FILL[st.fill] || FILL.warn,
        `+${st.pts.toFixed(1)}`,
        [l1, st.resolved ? `${l2} (resolved)`.trim() : l2],
        st.key,
        <line
          x1={xa - bw * 0.24}
          x2={xa}
          y1={y(st.from)}
          y2={y(st.from)}
          className="wf-connector"
        />,
      ),
    );
  });
  parts.push(
    bar(steps.length + 1, lo, end, FILL.ok, `${Math.round(end)}%`, ["If all closed", ""]),
  );
  gaps.forEach((g, i) => {
    const [l1, l2] = splitLabel(shortLabel(g.key));
    parts.push(
      <g key={`gap-${g.key}`} data-testid="waterfall-gap-bar" data-gap={g.key}>
        {bar(
          steps.length + 2 + i,
          g.from,
          g.to,
          FILL.muted,
          `+${g.pts.toFixed(1)}`,
          [l1, l2],
          null,
          <title>{`${g.label}: $${g.dollars.toFixed(2)} on ${g.claims} claim${g.claims === 1 ? "" : "s"}`}</title>,
        )}
      </g>,
    );
  });
  if (other) {
    const parts_ = other.components
      .map((c) => `${c.label}: $${Number(c.dollars).toFixed(2)}`)
      .join("; ");
    parts.push(
      <g
        key="other-bar"
        data-testid="waterfall-other-bar"
        data-points={other.pts.toFixed(1)}
      >
        {bar(
          steps.length + 2 + gaps.length,
          other.from,
          other.to,
          FILL.muted,
          `+${other.pts.toFixed(1)}`,
          ["Other", "to 100%"],
          "other",
          <title>{`Other, to 100%: $${other.dollars.toFixed(2)}${parts_ ? ` (${parts_})` : ""}`}</title>,
        )}
      </g>,
    );
  }

  return (
    <div className="wf-chart" data-testid="waterfall-chart">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Points to ${target}%`}
      >
        {grid}
        <line
          x1={PX}
          x2={W - 8}
          y1={y(target)}
          y2={y(target)}
          className="wf-target"
        />
        <text
          x={PX + 6}
          y={y(target) - 5}
          fontSize="10"
          textAnchor="start"
          className="wf-target-label"
          data-testid="waterfall-target-label"
        >
          {target}% target
          {unexplained > 0.05
            ? ` · ${unexplained.toFixed(1)} pts still unexplained`
            : ""}
        </text>
        {parts}
      </svg>
    </div>
  );
}
